import { Component, EventEmitter, Input, Output, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { EnumToArray, UserRolesEnum } from '@buspro/shared/common';
import { ChangeCompanyNameDto } from '@buspro/web/apis';
import { CompaniesApi, TenantUsersDto } from '@buspro/web/apis';
import { ErrorDisplayComponent, ExtendedProblemDetail, useFormErrorHandler } from '@buspro/web/core';
import { injectMutation } from '@tanstack/angular-query-experimental';
import { NzFormControlComponent, NzFormDirective, NzFormItemComponent, NzFormLabelComponent } from 'ng-zorro-antd/form';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { NzModalComponent, NzModalContentDirective, NzModalTitleDirective } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'tenant-change-company-name-modal',
  templateUrl: './change-company-name-modal.component.html',
  styleUrls: ['./change-company-name-modal.component.css'],
  imports: [
    NzModalComponent,
    NzModalTitleDirective,
    NzModalContentDirective,
    ReactiveFormsModule,
    NzFormItemComponent,
    NzFormLabelComponent,
    NzFormControlComponent,
    NzInputDirective,
    NzFormDirective,
    ErrorDisplayComponent,
  ],
  standalone: true,
})
export class ChangeCompanyNameModalComponent {
  private formBuilder = inject(FormBuilder);
  private companiesApi = inject(CompaniesApi);
  private notification = inject(NzNotificationService);

  // Standardized error handling
  private readonly formErrorHandler = useFormErrorHandler();
  showError = signal(false);
  errorDetails: ExtendedProblemDetail | null = null;

  @Input() show = false;
  @Output() hide = new EventEmitter(true);
  rolesList = EnumToArray(UserRolesEnum);
  form = this.formBuilder.group({
    companyName: ['', [Validators.required]],
  });

  private _user!: TenantUsersDto;

  get user(): TenantUsersDto {
    return this._user;
  }

  @Input()
  set user(value: TenantUsersDto) {
    if (value) {
      this._user = value;
      this.form.get('companyName')!.setValue(value.company?.name);
    }
  }

  changeCompanyNameMutation = injectMutation(() => ({
    mutationFn: (payload: ChangeCompanyNameDto) =>
      lastValueFrom(this.companiesApi.companyControllerChangeCompanyName({ body: payload })),
    onSuccess: () => {
      this.clearErrors();
      this.form.reset();
      this.notification.success('Changes applied', '');
      this.hide.emit();
      window.location.reload();
    },
    onError: (error: any) => {
      const result = this.formErrorHandler.handleFormError(error, { formName: 'tenant-change-company-name-form' });
      this.errorDetails = result.problemDetail ?? null;
      this.showError.set(!!this.errorDetails);
    },
  }));

  onHide() {
    this.hide.emit(true);
    this.show = false;
  }

  private clearErrors() {
    this.showError.set(false);
    this.errorDetails = null;
  }

  onDismissError() {
    this.clearErrors();
  }

  update() {
    this.clearErrors();
    const payload: ChangeCompanyNameDto = {
      newName: this.form.get('companyName')!.value!,
    };

    this.changeCompanyNameMutation.mutate(payload);
  }
}
